import { api } from './api';

/** Read a picked file into a data: URL (base64) for upload. */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Upload an image to the shared form-image store and return its serving
 * URL. Builders store the URL, never base64 in the schema, or the 700ms
 * draft autosave would re-send megabytes per keystroke.
 */
export async function uploadFormImage(file: File): Promise<string> {
  const base64 = await readAsDataUrl(file);
  const res = await api<{ url: string }>('/api/admin/onboarding/form-images', {
    method: 'POST',
    body: JSON.stringify({ base64, filename: file.name }),
  });
  return res.url;
}

/**
 * Upload a PDF (training slide `pdf` block) to the shared file store and
 * return its serving URL. Same store/route as images, served as a PDF.
 */
export async function uploadFormFile(file: File): Promise<string> {
  const base64 = await readAsDataUrl(file);
  const res = await api<{ url: string }>('/api/admin/onboarding/form-files', {
    method: 'POST',
    body: JSON.stringify({ base64, filename: file.name }),
  });
  return res.url;
}

type VideoUploadInit =
  | { mode: 's3'; uploadUrl: string; headers: Record<string, string>; url: string }
  | { mode: 'direct'; uploadUrl: string };

/** PUT a file with upload-progress, returning the response body text. */
function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText);
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

/**
 * Upload a self-hosted training video (the `video` block). Real mp4s exceed
 * the base64-over-JSON image path and the nginx body cap, so the bytes go
 * straight to S3 via a presigned PUT (the API only mints the target). The dev
 * filesystem backend can't presign, so it streams to a local raw endpoint
 * instead — the returned serving URL is identical either way.
 */
export async function uploadFormVideo(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const init = await api<VideoUploadInit>('/api/admin/onboarding/form-video-uploads', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, size: file.size }),
  });
  if (init.mode === 's3') {
    await putWithProgress(init.uploadUrl, file, init.headers, onProgress);
    return init.url;
  }
  // Dev: the raw endpoint stores the bytes and returns the serving URL.
  const body = await putWithProgress(
    init.uploadUrl,
    file,
    { 'Content-Type': 'application/octet-stream' },
    onProgress,
  );
  return (JSON.parse(body) as { url: string }).url;
}

/** Extensions routed through the direct-PUT video path in form file fields. */
export const FORM_VIDEO_EXTS = ['mp4', 'm4v', 'webm', 'mov'];

type FieldVideoInit =
  | { mode: 's3'; uploadUrl: string; headers: Record<string, string>; key: string }
  | { mode: 'direct'; uploadUrl: string };

interface FieldVideoSaved {
  name: string;
  key: string;
  content_type?: string;
}

/**
 * Upload a video into an inductee form file field. Same shape as the admin
 * training-video path (presigned PUT on S3, streamed raw endpoint in dev)
 * but task/field scoped, and the completed upload is registered on the
 * form draft so it survives a refresh like any other picked file.
 */
export async function uploadFormFieldVideo(
  taskId: string,
  fieldKey: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<FieldVideoSaved> {
  const init = await api<FieldVideoInit>(
    `/api/me/onboarding/forms/${taskId}/video-uploads`,
    {
      method: 'POST',
      body: JSON.stringify({ field_key: fieldKey, filename: file.name, size: file.size }),
    },
  );
  if (init.mode === 's3') {
    await putWithProgress(init.uploadUrl, file, init.headers, onProgress);
    return api<FieldVideoSaved>(`/api/me/onboarding/forms/${taskId}/video-uploads/confirm`, {
      method: 'POST',
      body: JSON.stringify({ key: init.key }),
    });
  }
  const body = await putWithProgress(
    init.uploadUrl,
    file,
    { 'Content-Type': 'application/octet-stream' },
    onProgress,
  );
  return JSON.parse(body) as FieldVideoSaved;
}
